import { Injectable } from '@angular/core';
import { ReturnSummaryDto } from '../../models/return.model';

/**
 * Holds the active cashering basket for the duration of the browser session so the
 * basket survives navigating away from the cashering page and back. State is mirrored
 * to sessionStorage, so it also survives a page reload but is cleared when the tab
 * closes. The basket is cleared explicitly on checkout or when the cashier clears the
 * customer.
 */
export interface CasheringBasketState {
  customerId: number;
  customerName: string;
  basketId: number | null;
  items: ReturnSummaryDto[];
}

@Injectable({ providedIn: 'root' })
export class CasheringBasketStore {
  private static readonly KEY = 'tdt.cashering.basket';
  private state: CasheringBasketState | null = null;

  constructor() {
    this.state = this.read();
  }

  /** The persisted basket, or null when there is none. */
  get(): CasheringBasketState | null {
    return this.state;
  }

  hasBasket(): boolean {
    return !!this.state && this.state.basketId !== null && this.state.items.length > 0;
  }

  /** Persist (or update) the active basket. */
  save(state: CasheringBasketState): void {
    this.state = state;
    try {
      sessionStorage.setItem(CasheringBasketStore.KEY, JSON.stringify(state));
    } catch {
      /* sessionStorage unavailable (private mode / quota) — in-memory copy still works */
    }
  }

  /** Drop the basket (after checkout or an explicit clear). */
  clear(): void {
    this.state = null;
    try {
      sessionStorage.removeItem(CasheringBasketStore.KEY);
    } catch {
      /* ignore */
    }
  }

  private read(): CasheringBasketState | null {
    try {
      const raw = sessionStorage.getItem(CasheringBasketStore.KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as CasheringBasketState;
      if (!parsed || typeof parsed.customerId !== 'number' || !Array.isArray(parsed.items)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
